import { useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Icon from "@/v2-ui/icon"
import BaseTabs from "@/v2-ui/tabs"
import Tabs from "@/v2-backoffice-client/components/tabs"
import { TooltipButton } from "@/v2-backoffice-client/components/button"
import NotificationFeed from "@/v2-backoffice-client/notification/feed/NotificationFeed"
import NotificationMarkAllReadButton from "@/v2-backoffice-client/notification/markRead/NotificationMarkAllReadButton"
import { useStaffNotificationsQuery } from "@/v2-backoffice-client/notification/feed/notification.feed.query"
import { useStaffNotificationUnreadCountQuery } from "@/v2-backoffice-client/notification/unreadCount/notification.unreadCount.query"
import { useNotificationCenter } from "@/v2-backoffice-client/notification/NotificationCenterProvider"
import { useNotificationDockResize } from "@/v2-backoffice-client/notification/notification.resize"
import {
  NOTIFICATION_CATEGORIES,
  NOTIFICATION_FEED_LIMIT,
  type NotificationCategory,
  type NotificationCategoryId,
} from "@/v2-backoffice-client/notification/notification.constants"
import type { StaffNotificationRow } from "@/v2-backoffice-client/notification/feed/NotificationFeedItem"

function isInCategory(
  notification: StaffNotificationRow,
  category: NotificationCategory,
) {
  return !category.codes || category.codes.includes(notification.Code)
}

/**
 * The notification center: a panel docked between the sidebar and the
 * routed page (NOT a route, NOT a popover — it stays put while the
 * operator works the pages and view sheets to the right of it). Filter
 * tabs group the feed by responsibility, each row carries a quick action
 * into the relevant workbench, and the whole thing survives navigation
 * because its open state lives in NotificationCenterProvider.
 */
function NotificationCenterDock() {
  const { isOpen, close } = useNotificationCenter()
  const { width, onResizeStart, onResetWidth } = useNotificationDockResize()

  const [categoryId, setCategoryId] = useState<NotificationCategoryId>("all")
  const [isUnreadOnly, setIsUnreadOnly] = useState(false)

  const { data, loading, error } = useStaffNotificationsQuery({
    variables: { limit: NOTIFICATION_FEED_LIMIT },
    skip: !isOpen,
  })
  const { data: unreadData } = useStaffNotificationUnreadCountQuery({
    skip: !isOpen,
  })
  const unreadCount = unreadData?.getStaffNotificationUnreadCount ?? 0

  if (!isOpen) return null

  const notifications = data?.getStaffNotifications ?? []
  const category =
    NOTIFICATION_CATEGORIES.find((entry) => entry.id === categoryId) ??
    NOTIFICATION_CATEGORIES[0]

  const visibleNotifications = notifications.filter(
    (notification) =>
      isInCategory(notification, category) &&
      (!isUnreadOnly || !notification.ReadAt),
  )

  function countUnread(entry: NotificationCategory) {
    return notifications.filter(
      (notification) =>
        !notification.ReadAt && isInCategory(notification, entry),
    ).length
  }

  const emptyLabel = isUnreadOnly
    ? "No unread notifications here — turn off the Unread filter to see handled ones."
    : category.emptyLabel

  return (
    <aside
      aria-label="Notification center"
      style={{ width }}
      className="notification-center-dock relative flex h-full shrink-0 flex-col border-r border-border"
    >
      {/* h-12 matches the sidebar's branding block, so this border-b
          continues the sidebar's under-logo line in one straight run. */}
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-4">
        <Icon src="bell" size={14} className="text-muted-foreground" />
        <h2 className="text-sm font-semibold leading-none">Notifications</h2>
        {unreadCount > 0 ? (
          <span className="rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold leading-none tabular-nums text-primary">
            {unreadCount > 99 ? "99+" : unreadCount} new
          </span>
        ) : null}
        <span className="-my-1 ml-auto flex items-center gap-0.5">
          {/* Unread-only filter lives up here (not on the tab row) so the
              category tabs get the dock's full width. */}
          <TooltipButton
            variant="ghost"
            size="tiny"
            iconSrc={isUnreadOnly ? "envelope-dot" : "envelope-open"}
            content={isUnreadOnly ? "Show all" : "Show unread only"}
            aria-pressed={isUnreadOnly}
            className={cn(
              isUnreadOnly
                ? "bg-primary/15 text-primary hover:bg-primary/25 hover:text-primary"
                : "text-muted-foreground hover:text-foreground",
            )}
            data-feature-code="UI_BackofficeNotifications_Dock_UnreadFilter"
            onClick={() => setIsUnreadOnly(!isUnreadOnly)}
          />
          {unreadCount > 0 ? <NotificationMarkAllReadButton /> : null}
          <TooltipButton
            variant="ghost"
            size="tiny"
            iconSrc="cross"
            content="Close notifications"
            className="text-muted-foreground hover:text-foreground"
            data-feature-code="UI_BackofficeNotifications_Dock_Close"
            onClick={close}
          />
        </span>
      </header>

      <div className="shrink-0 border-b border-border px-2 py-1.5">
        <BaseTabs
          value={categoryId}
          onValueChange={(value) =>
            setCategoryId(value as NotificationCategoryId)
          }
          className="ui-tabs min-w-0"
        >
          {/* Chrome-less strip: the triggers' own active pills are the
              only surfaces — the boxed default reads heavy at dock width. */}
          <Tabs.List className="flex-wrap justify-start gap-0.5 border-0 bg-transparent p-0">
            {NOTIFICATION_CATEGORIES.map((entry) => {
              const entryUnread = countUnread(entry)
              return (
                <Tabs.Trigger
                  key={entry.id}
                  value={entry.id}
                  count={entryUnread > 0 ? entryUnread : undefined}
                  data-feature-code="UI_BackofficeNotifications_Dock_Tab"
                >
                  {entry.label}
                </Tabs.Trigger>
              )
            })}
          </Tabs.List>
        </BaseTabs>
      </div>

      <NotificationFeed
        className="min-h-0 flex-1 overflow-y-auto"
        notifications={visibleNotifications}
        isLoading={loading}
        error={error}
        emptyLabel={emptyLabel}
      />

      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize notification center"
        className="notification-center-dock-resize-handle"
        onMouseDown={onResizeStart}
        onDoubleClick={onResetWidth}
      />
    </aside>
  )
}

export default NotificationCenterDock
